/**
 * Boot Error Fallback Component
 * 
 * BUG FIX v32: Non-white-screen fallback if boot fails
 * Shows a user-friendly error UI instead of blank screen
 */

interface BootErrorFallbackProps {
  error?: Error | string;
  onRetry?: () => void;
  onReload?: () => void;
}

export function BootErrorFallback({ error, onRetry, onReload }: BootErrorFallbackProps) {
  const errorMessage = error instanceof Error ? error.message : error || 'Unknown error';
  const errorStack = error instanceof Error ? error.stack : undefined;

  const handleReload = () => {
    if (onReload) {
      onReload();
    } else {
      window.location.reload();
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-md w-full rounded-lg border border-gray-200 bg-white p-6 shadow-lg text-center">
        <div className="mb-4">
          <svg
            className="mx-auto h-16 w-16 text-amber-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="mb-2 text-xl font-semibold text-gray-900">
          App Failed to Start
        </h1>
        <p className="mb-4 text-sm text-gray-600">
          We encountered an issue while starting the app. This is usually temporary and can be fixed by reloading.
        </p>
        {errorMessage && (
          <div className="mb-4 rounded-lg bg-gray-50 p-3 text-left">
            <p className="text-xs font-medium text-gray-700 mb-1">Error details:</p>
            <p className="text-xs text-gray-600 break-words">{errorMessage}</p>
          </div>
        )}
        <div className="flex gap-3">
          <button
            onClick={handleReload}
            className="flex-1 rounded-lg bg-[#007c86] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#006a73] focus:outline-none focus:ring-2 focus:ring-[#007c86] focus:ring-offset-2 transition-colors"
          >
            Reload App
          </button>
          {onRetry && (
            <button
              onClick={handleRetry}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#007c86] focus:ring-offset-2 transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
        {import.meta.env.DEV && errorStack && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
              Technical details (development only)
            </summary>
            <pre className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs text-gray-800 max-h-40">
              {errorStack}
            </pre>
          </details>
        )}
        <p className="mt-4 text-xs text-gray-500">
          If this problem persists, try clearing your browser cache or contact support.
        </p>
      </div>
    </div>
  );
}

